/**
 * Meadow life + water — everything repeated is instanced; wind and flapping
 * live entirely in the vertex shader; the water shader fakes fresnel + sun
 * glitter off analytic wave normals.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { mulberry32 } from './textures';
import { groundHeight, groundSlope, PLATEAU_R, waterLevel } from './terrain';

export interface ScatterBuild {
  group: THREE.Group;
  update(t: number, sunDir: THREE.Vector3, skyColor: THREE.Color): void;
  dispose(): void;
}

const GRASS_COUNT = 70000;

function makeGrassGeometry(): THREE.BufferGeometry {
  // Crossed quads, pivot at the base, height gradient in uv.y for wind bend.
  const geo = new THREE.PlaneGeometry(0.05, 0.18);
  geo.translate(0, 0.11, 0);
  const second = geo.clone();
  second.rotateY(Math.PI / 2);
  const merged = mergeGeometries([geo, second], false);
  geo.dispose();
  second.dispose();
  return merged ?? new THREE.PlaneGeometry(0.05, 0.18).translate(0, 0.11, 0);
}

export function buildScatter(): ScatterBuild {
  const group = new THREE.Group();
  group.name = 'castle-scatter';
  const rng = mulberry32(77);

  // --- placement ------------------------------------------------------------
  const treeXforms: THREE.Matrix4[] = [];
  const rockXforms: THREE.Matrix4[] = [];
  const grassPos: THREE.Vector3[] = [];
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const s = new THREE.Vector3();

  let placedTrees = 0;
  let attempts = 0;
  while (placedTrees < 130 && attempts < 4000) {
    attempts += 1;
    const a = rng() * Math.PI * 2;
    const r = 78 + rng() * 190;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    if (Math.abs(z) < 26 && z > 0) continue; // keep the approach road clear
    const slope = groundSlope(x, z);
    if (slope > 0.38) continue;
    const y = groundHeight(x, z);
    e.set(0, rng() * Math.PI * 2, 0);
    q.setFromEuler(e);
    const sc = 0.8 + rng() * 1.7;
    s.set(sc, sc * (0.9 + rng() * 0.4), sc);
    m.compose(new THREE.Vector3(x, y, z), q, s);
    treeXforms.push(m.clone());
    placedTrees += 1;
  }

  for (let i = 0; i < 64; i += 1) {
    const a = rng() * Math.PI * 2;
    const r = PLATEAU_R + 10 + rng() * 170;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const sc = 0.4 + rng() * 1.9;
    e.set(rng() * Math.PI, rng() * Math.PI, 0);
    q.setFromEuler(e);
    m.compose(
      new THREE.Vector3(x, groundHeight(x, z) - 0.15 * sc, z),
      q,
      s.set(sc, sc * 0.72, sc),
    );
    rockXforms.push(m.clone());
  }

  let grassPlaced = 0;
  attempts = 0;
  // Clumped sward: tuft centres with 5-9 blades each, so local density reads
  // as grass and the gaps read as meadow instead of planted stakes.
  const bladeTmp: THREE.Vector3[] = [];
  while (grassPlaced < GRASS_COUNT && attempts < 40000) {
    attempts += 1;
    const a = rng() * Math.PI * 2;
    const r = Math.sqrt(rng()) * 125;
    const cx = Math.cos(a) * r;
    const cz = Math.sin(a) * r;
    if (groundSlope(cx, cz) > 0.5) continue;
    if (r > 44 && r < 60) continue; // keep the moat ring clear
    const cy = groundHeight(cx, cz);
    const bladesPerTuft = 5 + Math.floor(rng() * 5);
    for (let b = 0; b < bladesPerTuft && grassPlaced < GRASS_COUNT; b += 1) {
      const ba = rng() * Math.PI * 2;
      const br = rng() * 0.28;
      bladeTmp.push(new THREE.Vector3(cx + Math.cos(ba) * br, cy, cz + Math.sin(ba) * br));
      grassPlaced += 1;
    }
  }
  grassPos.push(...bladeTmp);

  // --- trees -----------------------------------------------------------------
  const trunkGeo = new THREE.CylinderGeometry(0.22, 0.4, 2.6, 7);
  trunkGeo.translate(0, 1.3, 0);
  const leafGeoA = new THREE.ConeGeometry(2.1, 3.4, 9);
  leafGeoA.translate(0, 3.9, 0);
  const leafGeoB = new THREE.ConeGeometry(1.55, 2.8, 9);
  leafGeoB.translate(0, 5.7, 0);
  const foliageGeo = mergeGeometriesSafe([leafGeoA, leafGeoB]);
  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5b432c, roughness: 1 });
  const leafMat = new THREE.MeshStandardMaterial({ color: 0x46603a, roughness: 0.95 });
  const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, treeXforms.length);
  treeXforms.forEach((x, i) => trunks.setMatrixAt(i, x));
  const leaves = new THREE.InstancedMesh(foliageGeo ?? trunkGeo, leafMat, treeXforms.length);
  treeXforms.forEach((x, i) => leaves.setMatrixAt(i, x));
  trunks.castShadow = true;
  leaves.castShadow = true;
  trunks.receiveShadow = true;
  leaves.name = 'meadow-trees';
  trunks.name = 'meadow-trunks';
  group.add(trunks, leaves);

  // --- rocks -------------------------------------------------------------------
  const rockGeo = new THREE.DodecahedronGeometry(0.8);
  const rockMat = new THREE.MeshStandardMaterial({ color: 0x8a857c, roughness: 0.95 });
  const rocks = new THREE.InstancedMesh(rockGeo, rockMat, rockXforms.length);
  rockXforms.forEach((x, i) => rocks.setMatrixAt(i, x));
  rocks.castShadow = true;
  rocks.receiveShadow = true;
  rocks.name = 'meadow-rocks';
  group.add(rocks);

  // --- grass --------------------------------------------------------------------
  const grassGeo = makeGrassGeometry();
  const phases = new Float32Array(grassPos.length);
  for (let i = 0; i < grassPos.length; i += 1) phases[i] = rng() * Math.PI * 2;
  grassGeo.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phases, 1));
  const grassTime = { value: 0 };
  const grassMat = new THREE.MeshLambertMaterial({ color: 0x77803f, side: THREE.DoubleSide });
  grassMat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = grassTime;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>\nuniform float uTime;\nattribute float aPhase;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float bendW = pow(uv.y, 1.7);
        transformed.x += sin(uTime * 1.9 + aPhase) * 0.24 * bendW;
        transformed.z += cos(uTime * 1.4 + aPhase * 1.3) * 0.14 * bendW;`,
      );
  };
  grassMat.customProgramCacheKey = () => 'castle-grass-wind';
  const grassInst = new THREE.InstancedMesh(grassGeo, grassMat, grassPos.length);
  grassPos.forEach((p, i) => {
    e.set(0, rng() * Math.PI, 0);
    q.setFromEuler(e);
    const sc = 0.7 + rng() * 0.9;
    m.compose(p, q, s.set(sc, sc * (0.8 + rng() * 0.7), sc));
    grassInst.setMatrixAt(i, m);
  });
  grassInst.frustumCulled = false;
  grassInst.receiveShadow = false;
  grassInst.name = 'meadow-grass';
  group.add(grassInst);

  // --- birds ----------------------------------------------------------------------
  const birdGeo = birdGeometry();
  const birdMat = new THREE.MeshBasicMaterial({ color: 0x1c1a18, side: THREE.DoubleSide });
  const BIRDS = 17;
  const birds = new THREE.InstancedMesh(birdGeo, birdMat, BIRDS);
  birds.frustumCulled = false;
  birds.name = 'meadow-birds';
  group.add(birds);
  const birdParams = Array.from({ length: BIRDS }, (_, i) => ({
    r: 80 + rng() * 60,
    h: 30 + rng() * 22,
    speed: 0.05 + rng() * 0.06,
    phase: (i / BIRDS) * Math.PI * 2,
    scale: 0.9 + rng() * 0.8,
  }));

  function updateBirds(t: number): void {
    for (let i = 0; i < BIRDS; i += 1) {
      const b = birdParams[i];
      const a = b.phase + t * b.speed;
      const x = Math.cos(a) * b.r;
      const z = Math.sin(a) * b.r;
      const y = groundHeight(0, 0) + b.h + Math.sin(t * 0.4 + b.phase * 3) * 2.4;
      const flap = 0.55 + Math.abs(Math.sin(t * 7 + b.phase)) * 0.9;
      e.set(0, -a - Math.PI / 2, 0);
      q.setFromEuler(e);
      m.compose(
        new THREE.Vector3(x, y, z),
        q,
        s.set(b.scale, b.scale * flap, b.scale),
      );
      birds.setMatrixAt(i, m);
    }
    birds.instanceMatrix.needsUpdate = true;
  }

  // --- moat water ---------------------------------------------------------------
  const waterUniforms = {
    uTime: { value: 0 },
    uSunDir: { value: new THREE.Vector3(0.4, 0.5, 0.6).normalize() },
    uSunColor: { value: new THREE.Color('#ffd9a0') },
    uDeep: { value: new THREE.Color('#17313a') },
    uSky: { value: new THREE.Color('#e8a06a') },
  };
  const waterGeo = new THREE.RingGeometry(44.5, 59, 96, 1);
  waterGeo.rotateX(-Math.PI / 2);
  const waterMat = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: waterUniforms,
    vertexShader: /* glsl */ `
      varying vec3 vWp;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWp = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uSunDir;
      uniform vec3 uSunColor;
      uniform vec3 uDeep;
      uniform vec3 uSky;
      varying vec3 vWp;
      void main() {
        vec2 p = vWp.xz;
        // Analytic ripple normal: sum of travelling waves.
        float h = 0.0;
        vec2 grad = vec2(0.0);
        for (int i = 0; i < 4; i++) {
          float fi = float(i);
          vec2 d = normalize(vec2(cos(fi * 2.3 + 0.7), sin(fi * 1.9)));
          float ph = dot(p, d) * (2.2 + fi * 1.4) + uTime * (1.4 + fi * 0.7);
          h += sin(ph);
          grad += cos(ph) * d * (2.2 + fi * 1.4);
        }
        vec3 n = normalize(vec3(-grad.x * 0.02, 1.0, -grad.y * 0.02));
        vec3 viewDir = normalize(cameraPosition - vWp);
        float fres = pow(1.0 - max(dot(viewDir, n), 0.0), 3.2);
        vec3 col = mix(uDeep * 0.8, uSky * 0.78, clamp(fres, 0.0, 1.0));
        vec3 hv = normalize(uSunDir + viewDir);
        float spec = pow(max(dot(n, hv), 0.0), 140.0);
        col += uSunColor * spec * 1.4;
        gl_FragColor = vec4(col, 0.93);
      }
    `,
  });
  const water = new THREE.Mesh(waterGeo, waterMat);
  const wl = waterLevel();
  // The moat dips below the plateau; water plane sits mid-dip, following the
  // plateau crown height everywhere (the dip is carved relative to it).
  water.position.y = wl;
  water.renderOrder = 1;
  water.name = 'moat-water';
  group.add(water);

  return {
    group,
    update(t, sunDir, skyColor) {
      grassTime.value = t;
      waterUniforms.uTime.value = t;
      waterUniforms.uSunDir.value.copy(sunDir);
      waterUniforms.uSky.value.copy(skyColor);
      updateBirds(t);
    },
    dispose() {
      [trunkGeo, leafGeoA, leafGeoB, rockGeo, grassGeo, birdGeo].forEach((g) => g.dispose());
      [trunkMat, leafMat, rockMat, grassMat, birdMat, waterMat].forEach((m2) => m2.dispose());
      foliageGeo?.dispose();
      waterGeo.dispose();
    },
  };
}

function mergeGeometriesSafe(geos: THREE.BufferGeometry[]): THREE.BufferGeometry | null {
  // positions + index only merge for our simple cone pairs.
  let vc = 0;
  let ic = 0;
  for (const g of geos) {
    vc += g.attributes.position.count;
    ic += g.index?.count ?? 0;
  }
  if (ic === 0) return null;
  const pos = new Float32Array(vc * 3);
  const nor = new Float32Array(vc * 3);
  const idx = new Uint32Array(ic);
  let vo = 0;
  let io = 0;
  for (const g of geos) {
    const p = g.attributes.position.array as Float32Array;
    const n = g.attributes.normal.array as Float32Array;
    const ix = g.index!.array as ArrayLike<number>;
    pos.set(p, vo * 3);
    nor.set(n, vo * 3);
    for (let i = 0; i < ix.length; i += 1) idx[io + i] = ix[i] + vo;
    vo += g.attributes.position.count;
    io += ix.length;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  out.setIndex(new THREE.BufferAttribute(idx, 1));
  return out;
}

function birdGeometry(): THREE.BufferGeometry {
  // Simple flapping V: two wing triangles meeting at the body point.
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array([
    0, 0, 0.35, 1.1, 0.12, -0.2, 0.25, 0.1, -0.28,
    0, 0, 0.35, 0.25, 0.1, -0.28, -1.1, 0.12, -0.2,
  ]);
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}



